import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Meta } from '@angular/platform-browser';
import { AsmAuthStorageService } from '@spartacus/asm/root';
import { OCC_USER_ID_ANONYMOUS, OCC_USER_ID_CURRENT, UserIdService } from '@spartacus/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, mergeMap, publishReplay, refCount, take } from 'rxjs/operators';
import { ASM_ENABLED_LOCAL_STORAGE_KEY } from 'src/app/spartacus/features/asm/asm-constant';
import { environment } from 'src/environments/environment';
import { StorageService } from '../storage/storage.service';

@Injectable({
  providedIn: 'root',
})
export class SiteContextService {
  headers = new HttpHeaders().set('Content-Type', 'application/json');
  occUrl = this.meta.getTag('name=occ-backend-base-url');
  url = `${this.occUrl.content}${environment.prefix}`;
  cacheWindowTime = 120000; // 2 minutes
  userId: string;

  private cachedBaseSiteId$: any = {};
  private cachedLanguages$: any = new Map<string, Observable<string[]>>();

  constructor(
    protected http: HttpClient,
    private meta: Meta,
    private authService: UserIdService,
    private asmAuthService: AsmAuthStorageService,
    private storageService: StorageService
  ) {
    if (
      this.storageService &&
      this.storageService.getLocalStorageItem(ASM_ENABLED_LOCAL_STORAGE_KEY) &&
      !this.storageService.getLocalStorageItem('initLoginPage')
    ) {
      this.asmAuthService
        .getTokenTarget()
        .pipe(mergeMap((csAgentToken) => (csAgentToken ? this.authService.getUserId() : of(null))))
        .subscribe((userId) => {
          if (userId && userId !== OCC_USER_ID_ANONYMOUS && userId !== OCC_USER_ID_CURRENT) {
            this.userId = userId;
          }
        });
    }
  }

  getShimanoSiteContexts$(businessType?: string, country?: string, language?: string): Observable<any> {
    let httpParams = new HttpParams();
    httpParams = httpParams.append('fields', 'DEFAULT');

    if (this.userId) {
      httpParams = httpParams.append('asmUser', this.userId);
    }

    if (businessType) {
      httpParams = httpParams.append('businessType', businessType);
    }

    if (country) {
      httpParams = httpParams.append('country', country);
    }

    if (language) {
      httpParams = httpParams.append('language', language);
    }

    const httpOptions = {
      headers: this.headers,
      params: httpParams,
    };

    return this.http.get(`${this.url}` + `site-contexts`, httpOptions).pipe(
      map((data: any) => data),
      catchError(this.errorHandl)
    );
  }

  getSelections$(language?: string): Observable<any> {
    let httpParams = new HttpParams();
    httpParams = httpParams.append('fields', 'DEFAULT');
    httpParams = httpParams.append('lang', language);

    const httpOptions = {
      headers: this.headers,
      params: httpParams,
    };

    return this.http.get(`${this.url}` + `site-contexts/selections`, httpOptions).pipe(
      map((data: any) => data),
      catchError(this.errorHandl)
    );
  }

  getBaseSiteId$(businessType: string, country: string): Observable<string> {
    const idString = [businessType, country].join();
    let httpParams = new HttpParams();
    httpParams = httpParams.append('businessType', businessType);
    httpParams = httpParams.append('country', country);

    const httpOptions = {
      headers: this.headers,
      params: httpParams,
    };

    if (!this.cachedBaseSiteId$[idString]) {
      this.cachedBaseSiteId$[idString] = this.http.get(`${this.url}` + `site-contexts/base-site`, httpOptions).pipe(
        map((data: any) => data.baseSiteId),
        publishReplay(1, this.cacheWindowTime),
        refCount(),
        take(1),
        catchError(this.errorHandl)
      );
    }

    return this.cachedBaseSiteId$[idString];
  }

  getLanguages$(businessType: string, country: string): Observable<string[]> {
    const idString = [businessType, country].join();
    let httpParams = new HttpParams();
    httpParams = httpParams.append('fields', 'DEFAULT');
    httpParams = httpParams.append('businessType', businessType);
    httpParams = httpParams.append('country', country);

    const httpOptions = {
      headers: this.headers,
      params: httpParams,
    };

    if (!this.cachedLanguages$[idString]) {
      this.cachedLanguages$[idString] = this.http.get(`${this.url}` + `site-contexts/languages`, httpOptions).pipe(
        map((data: any) => data.languages.map((language: any) => language.isocode)),
        publishReplay(1, this.cacheWindowTime),
        refCount(),
        take(1),
        catchError(this.errorHandl)
      );
    }

    return this.cachedLanguages$[idString];
  }

  errorHandl(error: any) {
    let errorMessage = '';
    if (error.error instanceof ErrorEvent) {
      errorMessage = error.error.message;
    } else {
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
    }
    return throwError(errorMessage);
  }
}
